import api, { prepareQueryParameters } from "@/store/api";
import {
	Asset,
	AssetDetail,
	AssetTransaction,
	AssetsGraphData,
	AssetsGraphDataDual,
	CompositionDetail,
	Portfolio,
	TimeFilter,
	Params
} from "@/types";
import {
	assetsHistoryToChartData,
	assetsHistoryToChartDataStrategy,
	getDateRange,
	getDateRangeDuration
} from "../helpers";

interface AssetDetails {
	[key: number]: AssetDetail;
}

interface AssetParams extends Params {
	investmentStrategyId: number;
	from?: Date;
	to?: Date;
}

const [from, to] = getDateRange(TimeFilter.MONTHS_3);
const emptyState = () => {
	return {
		loading: true,
		loadingCompositionDetail: true,
		loadingCashStates: true,
		cashStates: [],
		compositionByAssets: [],
		polarisComposition: [],
		polarisValues: [],
		shortHistory: null,
		loadingShortHistory: true,
		loadingList: true,
		filterId: TimeFilter.MONTHS_3,
		list: {
			assets: [] as Asset[],
			values: []
		},
		from,
		to,
		assetsGraphData: { labels: [], datasets: [] } as AssetsGraphDataDual,
		details: {} as AssetDetails,
		transactions: [] as AssetTransaction[],
		compositionDetail: [] as CompositionDetail[],
		compositionDetailHeading: "",
	}
};

const state = emptyState();
type State = typeof state;

export default {
	namespaced: true,
	state: emptyState(),
	getters: {},
	actions: {
		loadAssetsHistory(store: any, params: AssetParams) {
			const { from, to } = store.state as State;
			params.filter = getDateRangeDuration(store.state.filterId);
			store.commit("loading", true);
			api
				.get(`asset-accounts/assets-history?${prepareQueryParameters(params)}`)
				.then(({ data }) => {
					store.commit("setGraphData",
						params.investmentStrategyId
							? assetsHistoryToChartDataStrategy(data.result)
							: assetsHistoryToChartData(data.result)
					);
					store.commit("loading", false);
				})
				.catch((error) => {
					store.commit("loading", false);
				});
		},
		loadShortHistory(store: any, params: AssetParams) {
			store.commit("setLoadingShortHistory", true);
			api
				.get(`asset-accounts/assets-short-history`)
				.then(({ data }) => {
					store.commit("setShortHistory", data.result);
					store.commit("setLoadingShortHistory", false);
				})
				.catch((error) => {
					console.log(error);
					store.commit("setLoadingShortHistory", false);
				});
		},
		compositionByAssets(store: any, params: AssetParams) {
			// store.commit("setLoading", true);
			api
				.get(`asset-accounts/composition-by-assets`)
				.then(({data}) => {
					store.commit("setCompositionByAssets", data.result);
					// store.commit("setLoading", false);
				})
				.catch((error) => {
					store.commit("setCompositionByAssets", []);
					// store.commit("setLoading", false);
				});
		},
		assets(store: any, params: any) {
			store.commit("loadingList", true);
			api
				.get(`asset-accounts/assets?${prepareQueryParameters(params)}`)
				.then(({ data }) => {
					store.commit("setList", data.result);
					store.commit("resetDetail");
					store.commit("loadingList", false);
				})
				.catch((error) => {
					store.commit("loadingList", false);
				});
		},
		assetDetail(store: any, { id, index, portfolio, strategyId } : { id: string; index: number; portfolio?: number; strategyId?: number }) {
			if (!store.state.details.hasOwnProperty(id)) {
				const [from, to] = getDateRange(TimeFilter.YEAR);
				const params = {
					id,
					from: from.toISOString(),
					to: to.toISOString(),
					portfolio: portfolio,
					investmentStrategyId: strategyId,
				};

				api
					.get(`asset-accounts/assetDetail?${prepareQueryParameters(params)}`)
					.then(({data}) => {
						store.commit("addDetail", { code: id, detail: data.result, index: index });
					})
					.catch((error) => {
						console.log(error);
					});
			}
		},
		assetTransactions(store: any, { code, index, portfolio }: { code: string; index: number, portfolio: string }) {
			if (!store.state.details.hasOwnProperty(code)) {
				const [from, to] = getDateRange(TimeFilter.YEAR);
				const params = {
					from: from.toISOString(),
					to: to.toISOString(),
				};
				api
					.get(`assets/transactions/${encodeURIComponent(code)}?${prepareQueryParameters(params)}`)
					.then((data) => {
						store.commit("addTransactions", { code, transactions: data.data, index });
					})
					.catch((error) => {
						console.log(error);
					});
			}
		},
		// assetCompositionDetail(
		// 	store: any,
		// 	{ assetType, assetTypeName, portfolio }: { assetType: string; assetTypeName: string; portfolio: Portfolio }
		// ) {
		// 	store.commit("setLoadingComposition", true);
		// 	const params = {
		// 		assetType,
		// 		portfolioId: portfolio.name,
		// 	};
		// 	api
		// 		.get(`assets/composition-detail?${prepareQueryParameters(params)}`)
		// 		.then((data) => {
		// 			store.commit("addCompositionDetail", { detail: data.data, assetTypeName });
		// 			store.commit("setLoadingComposition", false);
		// 		})
		// 		.catch((error) => {
		// 			console.log(error);
		// 		});
		// },
		cashState(store: any) {
			store.commit('setCashStatesLoading', true);

			api
				.get(`asset-accounts/clients-cash`)
				.then((data) => {
					store.commit('setCashStates', data.data.result);
					store.commit('setCashStatesLoading', false);
				})
				.catch((error) => {
					console.log(error);
				});
		},
	},
	mutations: {
		setCashStatesLoading(state: State, data: boolean) {
			state.loadingCashStates = data;
		},
		setCashStates(state: State, data: any) {
			state.cashStates = data;
		},
		setCompositionByAssets(state: State, data: any) {
			state.compositionByAssets = data;
		},
		setPolarisComposition(state: State, data: any) {
			state.polarisComposition = data;
		},
		setPolarisValues(state: State, data: any) {
			state.polarisValues = data;
		},
		setShortHistory(state: State, data: any) {
			state.shortHistory = data;
		},
		setLoading(state: State, data: boolean) {
			state.loading = data;
		},
		setLoadingComposition(state: State, data: boolean) {
			state.loadingCompositionDetail = data;
		},
		setLoadingShortHistory(state: State, loading: boolean) {
			state.loadingShortHistory = loading;
		},
		selectFilter(state: State, filterId: TimeFilter) {
			state.filterId = filterId;
			if (filterId !== TimeFilter.CUSTOM) {
				const [from, to] = getDateRange(filterId);
				state.from = from;
				state.to = to;
			}
		},
		setGraphData(state: State, data: AssetsGraphDataDual) {
			state.assetsGraphData = data;
		},
		loading(state: State, loading: boolean) {
			state.loading = loading;
		},
		loadingList(state: State, loading: boolean) {
			state.loadingList = loading;
		},
		setCustomFilter(state: State, { from, to }: { from: Date; to: Date }) {
			state.from = from;
			state.to = to;
		},
		setList(state: State, list: any) {
			state.list = list;
		},
		resetDetail(state: State) {
			state.details = {};
		},
		addDetail(state: State, { code, detail, index }: { code: string; detail: AssetDetail; index: number }) {
			state.details = { ...state.details, [code]: detail };
			const assets = state.list.assets;
			assets[index].detail = detail;
			state.list.assets = [...assets];
		},
		// addTransactions(state: State, { code, transactions, index }: { code: string; transactions: AssetTransaction[]; index: number }) {
		// 	state.transactions = { ...state.transactions, [code]: transactions };
		// 	const assets = state.list;
		// 	assets[index].transactions = transactions;
		// 	state.list = [...assets];
		// },
		// addCompositionDetail(
		// 	state: State,
		// 	{ detail, assetTypeName }: { detail: CompositionDetail[]; assetTypeName: string }
		// ) {
		// 	state.compositionDetail = detail;
		// 	state.compositionDetailHeading = assetTypeName;
		// },
		reset (state: any) {
			Object.assign(state, emptyState());
		}
	},
};
